import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory15() {
  const { lesson, course, accent } = findLesson("music-theory", "15");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT15-Chord-Classification-Animated.mp4"
        caption="3 lenses on the same C-F-G-C — emotion, function, cadence"
        hint="≈ 26 sec · watch first"
      />
      <Chapter number="15" eyebrow="Music Theory · Chapter Fifteen" title="Three lenses on the same seven chords">
        <Prose>
          <p>
            Every major key gives you exactly seven chords. We've spelled them, mapped
            them on the circle, and built them as triads. But knowing what they
            <em> are</em> doesn't tell you how to <em>use</em> them. This chapter offers
            three complementary classifications — three lenses that group the same seven
            chords differently, each lens revealing a different musical truth.
          </p>
          <p>
            Lens one is <strong>raw emotion</strong> — happy, sad, scary. Lens two is
            <strong> energy state</strong> — stable, in motion, demanding resolution.
            Lens three is <strong>cadence</strong> — which pairs of chords work together
            as musical sentences. Together they give you a vocabulary for choosing
            chords on purpose, instead of by trial and error.
          </p>
        </Prose>
        <MTQuote>
          These are, you could say, three decent ways of grouping or classifying your basic
          triads. One is by emotion, the other is by functional harmony or, I'll call it,
          energy state, stable or unstable, and last but not least you're classifying them
          based on the cadence.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Lens one" title="Raw emotion — major, minor, diminished">
        <Prose>
          <p>
            The simplest grouping is the one our ears already know. Out of the seven
            triads in a major key, three are major (the I, IV and V), three are minor
            (the ii, iii and vi), and one is diminished (the vii°). Major chords feel
            bright and uplifting. Minor chords feel serious or melancholy. The
            diminished chord feels uncertain, anxious, suspended.
          </p>
          <p>
            That's why nursery rhymes, patriotic anthems and other defiantly-positive
            music tend to lean on the three major chords. Songs of solitude and
            heartbreak — "Ain't No Sunshine When She's Gone" is the canonical example —
            lean on the minors. And the diminished chord, used sparingly, brings the
            anxiety needed for tension before resolution.
          </p>
        </Prose>

        <TheoryBox label="Mood map · C major">
          <ul className="space-y-1 text-[14px]">
            <li><strong>Happy / bright</strong>: C major (I), F major (IV), G major (V).</li>
            <li><strong>Sad / serious</strong>: D minor (ii), E minor (iii), A minor (vi).</li>
            <li><strong>Tense / anxious</strong>: B diminished (vii°).</li>
          </ul>
        </TheoryBox>

        <Prose>
          <p>
            One useful colour you can mix from this palette is the
            <strong> minor → three-major</strong> sequence. Take a single minor chord and
            follow it with three major chords in a row — the result feels brave or
            uplifting against odds. "I Walk Alone" by Green Day uses that exact device:
            a minor chord pulling against a chain of majors, like a hero leaning into
            the wind. So even within the simple "happy/sad" framing, mixing chord
            qualities lets you write more than one mood per song.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Lens two" title="Energy state — tonic, predominant, dominant">
        <Prose>
          <p>
            The second lens is <strong>functional harmony</strong>. Forget for a moment
            whether a chord is major or minor and ask instead: when this chord is
            playing, do we feel <em>at rest</em>, <em>in motion</em>, or <em>about to
            resolve</em>? That single question splits the seven chords into three
            functional groups.
          </p>
        </Prose>

        <Steps>
          <Step>
            <strong>Tonic chords</strong> — the I and the vi. These are the home chords.
            Play C major and A minor in any combination and the music feels relaxed,
            gravitationally settled. The vi works as a tonic because it shares two of
            its three notes with the I — they're nearly the same chord wearing
            different hats.
          </Step>
          <Step>
            <strong>Predominant chords</strong> — the ii and the IV (sometimes called
            <em> sub-dominant</em>). These are in-motion chords. They feel unstable, but
            their instability points <em>toward</em> a dominant chord rather than
            directly back to the tonic. They're the comma in a musical sentence.
          </Step>
          <Step>
            <strong>Dominant chords</strong> — the V and the vii° (often combined as
            V7, which is just the V triad with a fourth note added). These are the
            high-tension chords. They want to resolve, almost always to the tonic.
            They're the question mark before the answer.
          </Step>
        </Steps>

        <TheoryBox label="The three energy states">
          <strong>Tonic</strong> = rest (I, vi). <strong>Predominant</strong> = in motion
          (ii, IV). <strong>Dominant</strong> = wants to resolve (V, vii°, V7). Most
          tonal music alternates between these three states like inhale, hold, exhale.
        </TheoryBox>

        <Prose>
          <p>
            The classic functional path is <strong>tonic → predominant → dominant →
            tonic</strong>. In C major: C → Dm or F → G or G7 → C. Walk that loop a few
            times and you have the bones of about half the popular music ever written.
            You can also chain predominants: Dm → F → G → C. Or end on a dominant for a
            cliffhanger feel: F → G, with no resolution back to C, leaves the listener
            hanging on the question.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Lens three" title="Cadences — the musical sentences">
        <Prose>
          <p>
            The third lens groups chords into <strong>cadences</strong> — short,
            recognisable two-chord progressions that have been in use for centuries.
            Cadences are the musical equivalent of common phrases: "thank you", "good
            morning", "the end". They're already worn smooth by use; using them is a
            shortcut to making your music feel finished.
          </p>
        </Prose>

        <Steps>
          <Step>
            <strong>Authentic cadence — V → I.</strong> The most common ending in tonal
            music. In C major: G → C. In F major: C → F. Sounds resolved, like a full
            stop.
          </Step>
          <Step>
            <strong>Plagal cadence — IV → I.</strong> The "amen" cadence. F → C in C
            major. Softer than the authentic, often heard at hymn endings. Works equally
            well with a minor IV (Fm → C) for a more reflective close.
          </Step>
          <Step>
            <strong>Deceptive cadence — V → vi.</strong> The plot twist. The ear expects
            V to resolve to I, but you give it vi instead — same family of stability,
            different colour. G → Am in C major. A great way to extend a phrase past
            its expected ending.
          </Step>
          <Step>
            <strong>Half cadence — anything → V.</strong> The cliffhanger. End a phrase
            on a dominant chord and the music feels unresolved. Useful for setting up
            choruses or driving a narrative forward without closing it off.
          </Step>
        </Steps>

        <Prose>
          <p>
            Beyond the four classical cadences sits the workhorse of jazz and modern
            pop: the <strong>ii–V–I</strong>. Three chords in a row, walking
            counter-clockwise around the circle of fifths: D minor → G major → C major
            in C major. You can major-ise the ii (D major → G → C), minor-ise the I
            (Dm → G → Cm), or extend either chord with a seventh for jazz colour. The
            ii–V–I shows up everywhere from Bossa Nova to the verses of countless
            standards because it cleanly walks predominant → dominant → tonic in three
            steps.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Try it" title="Build any chord, any quality, any root">
        <Prose>
          <p>
            The chord builder below covers all four families this course works with:
            dyads (two notes), triads (three), sevenths (four), and extensions (five
            or more). Click through and listen — and as you do, place each chord
            mentally in the three lenses we just learned. Is it major, minor or
            diminished (lens 1)? Is it tonic, predominant or dominant (lens 2)? What
            cadence does it suggest (lens 3)?
          </p>
        </Prose>

        <ChordBuilder root="C" families={["dyad", "triad", "seventh"]} />

        <Glossary>
          <Term name="Functional harmony" def="The framework of tonic / predominant / dominant. Classifies chords by their pull toward or away from the home key." />
          <Term name="Cadence" def="A short pair of chords that mark the end of a musical phrase. Authentic, plagal, deceptive and half are the four standard kinds." />
          <Term name="Tonic chord" def="A chord that feels like home — the I or vi in a major key." />
          <Term name="Predominant" def="A chord in motion that points toward a dominant — the ii or IV. Sometimes called sub-dominant." />
          <Term name="Dominant" def="A high-tension chord that wants to resolve, usually to the tonic — the V, the vii°, or the V7." />
          <Term name="ii–V–I" def="The most ubiquitous cadence in modern music. Walks predominant → dominant → tonic in three counter-clockwise steps around the circle of fifths." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Putting all three lenses to work" title="A quick composition exercise">
        <Prose>
          <p>
            Pick a key — C major works fine — and write four short progressions, one
            per lens. <strong>Emotion:</strong> three majors in a row, then three minors
            in a row; notice how the mood pivots. <strong>Energy:</strong> tonic →
            predominant → dominant → tonic, four times around. <strong>Cadence:</strong>
            authentic, plagal, deceptive, and half, in sequence; record yourself and
            listen for how each one ends a phrase differently. <strong>Combination:</strong>
            string two ii–V–I cadences together — one ending on C major, the next
            ending on F major — and watch how the music modulates without leaving the
            same set of seven chords.
          </p>
          <p>
            None of these lenses is more correct than the others. They're three ways of
            looking at the same furniture. The musicians who feel most fluent are the
            ones who can switch lenses on the fly — choosing emotional colour one
            moment, energy state the next, and cadence right after that.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/15a-major-minor-dim-aug-sus-with-7ths.png"
          caption="Jason's three-method classification chart. Method 1: by base triad (Major / Minor / Diminished / Augmented / Suspended). Method 2: by 7th flavour (Maj7 / m7 / Dom7 / Min7♭5 / Dim7 / Aug7 / 7sus4). Method 3: by interval stack. Same chord, three legitimate names — pick the one that fits the song's vocabulary."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-16"
        label="Chapter 16 · Carnatic Scale Theory - Melakarta Ragas"
        why="With Western chord classification mapped, the next move is to widen the lens — and study the seventy-two parent scales of Carnatic music as a system for generating every seven-note scale you'll ever need."
      />
    </LessonShell>
  );
}
